import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import {
  useVerifyCredentialsMutation,
  useVerifyCredentialsOtpMutation,
} from '../store/authApi';
import OtpInput from '../components/common/OtpInput';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/routes';
import ChangePasswordModal from '../components/common/ChangePasswordModal';

// One card per portal we support. `requires` is the user_type that must
// match for the card to be enabled. `individual` is special — every
// signed-in user can open their personal dashboard regardless of role.
const PORTALS = [
  {
    id:       'individual',
    title:    'Individual',
    desc:     'See your personal lab reports, trends, upcoming tests and bookings.',
    icon:     PersonOutlineIcon,
    accent:   '#C97D08',
    route:    ROUTES.DASHBOARD,
    requires: null,                 // always open
  },
  {
    id:       'partner',
    title:    'Medical Professional',
    desc:     'View patient reports referred to you, raise queries, and access your clinical dashboard.',
    icon:     LocalHospitalOutlinedIcon,
    accent:   '#3D7A52',
    route:    ROUTES.MED_DASHBOARD,
    // Both centre-admin partners AND doctor-mode sessions land on
    // the Medical Portal, so either user_type unlocks this card.
    requires: ['partner', 'doctor'],
  },
  {
    id:       'hospital',
    title:    'Hospital / Lab',
    desc:     'Process patient samples, manage requisitions, and review your lab queue.',
    icon:     ScienceOutlinedIcon,
    accent:   '#3D7A52',
    route:    ROUTES.LAB_DASHBOARD,
    requires: 'hospital',
  },
  {
    id:       'organization',
    title:    'Corporate',
    desc:     "Manage your company's employee wellness reports, health programs, and analytics.",
    icon:     BusinessOutlinedIcon,
    accent:   '#3D7A52',
    route:    ROUTES.CORP_DASHBOARD,
    requires: 'organization',
  },
];

// Friendly "why is this locked" toast copy, keyed by the card the user
// tried to open. Keep it short — toast trunctates aggressively.
const LOCKED_COPY = {
  partner:      "You're not registered as a partner — this portal is for medical professionals.",
  hospital:     "You're not registered as a hospital / lab — this portal is for lab staff.",
  organization: "You're not registered as an organization — this portal is for corporate accounts.",
};

const Reports = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, setSession, uhidVerified } = useAuth();
  const { showToast } = useUI();

  // Patient-only UHID + password gate. Patients hit this page from
  // the navbar's "My Reports" link; before showing report data we
  // ask them to confirm their UHID + password, then enter the OTP
  // we send to their registered phone. Doctors / lab partners /
  // corporates skip the gate — their dashboards are role-gated
  // already.
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [uhid, setUhid] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [otp, setOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [authErr, setAuthErr] = useState('');
  // Pops the forced change-password modal when the OTP step returns
  // must_change_password === false (i.e. patient hasn't set their
  // own password yet — still on the default 'trustlab@2026' assigned
  // by the eDOS sync). Field semantics: false = not changed yet,
  // true = changed.
  const [showChangePwd, setShowChangePwd] = useState(false);

  const [verifyCredentials,    { isLoading: verifying }]    = useVerifyCredentialsMutation();
  const [verifyCredentialsOtp, { isLoading: verifyingOtp }] = useVerifyCredentialsOtpMutation();

  // Redirect to login if not signed in (bookmark / direct URL case).
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?next=${encodeURIComponent(ROUTES.REPORTS)}`, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Decide synchronously (during render) whether to skip this page
  // entirely and bounce the user to their portal. Using a declarative
  // <Navigate> below avoids the flicker that a useEffect-based
  // redirect causes (form paints once, then disappears).
  //
  // Cases that skip the gate (all require uhidVerified for the session):
  //   patient + has UHID → /dashboard
  //   partner / doctor   → /portal/medical
  //   hospital           → /portal/lab
  //   organization       → /portal/corporate
  //
  // Patient also requires user.uhid to actually exist — uhidVerified
  // can persist in localStorage past an admin unlinking, and without
  // a UHID the dashboard has nothing to show.
  const skipGateTarget =
    !isAuthenticated || !uhidVerified
      ? null
    : (user?.user_type === 'patient' && user?.uhid)
      ? ROUTES.DASHBOARD
    : (user?.user_type === 'partner' || user?.user_type === 'doctor')
      ? ROUTES.MED_DASHBOARD
    : (user?.user_type === 'hospital')
      ? ROUTES.LAB_DASHBOARD
    : (user?.user_type === 'organization')
      ? ROUTES.CORP_DASHBOARD
      : null;

  // Decorate the static PORTALS list with a per-user `enabled` flag so
  // the card visuals + click handlers both know whether the user has
  // access. Individual is always enabled; role cards match their
  // owner's user_type — `requires` is either a single string or an
  // array of acceptable types (the Medical Professional card uses an
  // array so both 'partner' and 'doctor' unlock it).
  const portals = useMemo(() => {
    if (!user) return [];
    return PORTALS.map((p) => {
      const req = p.requires;
      const enabled = req === null
        || (Array.isArray(req) ? req.includes(user.user_type) : user.user_type === req);
      return { ...p, enabled };
    });
  }, [user]);

  if (!user) return null;

  // Already verified → skip the gate. Returns the redirect during
  // render so the role-picker / verification form never paints.
  if (skipGateTarget) {
    return <Navigate to={skipGateTarget} replace />;
  }

  const isPatient = user.user_type === 'patient';

  const onCardClick = (p) => {
    if (p.enabled) {
      navigate(p.route);
      return;
    }
    showToast(LOCKED_COPY[p.id] || 'You do not have access to this portal.', 'error');
  };

  // Step 1: validate UHID + password. Server checks the password and
  // dispatches a 6-digit OTP to the registered phone. We don't get a
  // session yet — only `requiresOtp + masked_phone`. Step 2 below
  // completes the login.
  const onUhidSubmit = async (e) => {
    e?.preventDefault?.();
    setAuthErr('');
    if (!uhid.trim()) return setAuthErr('Enter your UHID');
    if (!pwd)         return setAuthErr('Enter your password');

    try {
      const res = await verifyCredentials({
        identifier: uhid.trim(),
        password: pwd,
      }).unwrap();
      setMaskedPhone(res.masked_phone || '');
      setStep('otp');
      showToast(`OTP sent to ${res.masked_phone || 'your registered mobile'}`);
    } catch (err) {
      setAuthErr(err?.data?.message || 'Could not verify UHID. Please try again.');
    }
  };

  // Step 2: verify the OTP for the same UHID's registered phone. On
  // success we get the JWT, mark uhidVerified so future visits to
  // /reports skip both steps and bounce straight to /dashboard.
  const onOtpSubmit = async (e) => {
    e?.preventDefault?.();
    setAuthErr('');
    if (!/^\d{6}$/.test(otp.trim())) return setAuthErr('Enter the 6-digit OTP');

    try {
      const res = await verifyCredentialsOtp({
        identifier: uhid.trim(),
        otp: otp.trim(),
      }).unwrap();
      setSession({ token: res.token, user: res.user, uhidVerified: true });
      // Patient hasn't set their own password yet → pop the modal.
      // Strict `=== false` so a legacy account where the field was
      // never written (undefined) skips the dialog.
      if (res.must_change_password === false) {
        setShowChangePwd(true);
        return;
      }
      showToast(`Welcome back, ${res.user.name}`);
      navigate(ROUTES.DASHBOARD);
    } catch (err) {
      setAuthErr(err?.data?.message || 'Invalid OTP. Please try again.');
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'var(--cream)',
        py: { xs: 2.5, md: 3.5 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' },
            gap: { xs: 4, md: 8 },
            alignItems: 'center',
          }}
        >
          {/* ── LEFT: welcome / explainer panel ───────────────── */}
          <Box sx={{ pt: { md: 2 } }}>
            <Typography
              sx={{
                fontSize: 11, fontWeight: 700, letterSpacing: '0.18em',
                textTransform: 'uppercase', color: 'var(--sage)', mb: 1.5,
              }}
            >
              Hello, {user.name?.split(' ')[0] || 'there'}
            </Typography>

            <Typography
              className="serif"
              sx={{
                fontSize: { xs: 30, md: 44 },
                fontWeight: 600,
                color: 'var(--ink)',
                lineHeight: 1.1,
                mb: 2,
              }}
            >
              Your TrustLab, your way.
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: 14, md: 15.5 },
                color: 'var(--stone)',
                lineHeight: 1.7,
                fontWeight: 300,
                mb: 3.5,
                maxWidth: 460,
              }}
            >
              Open the portal that fits today's task. Every TrustLab user gets their
              personal reports dashboard; professional portals unlock automatically
              for the accounts they're registered under.
            </Typography>

            {/* Trust-note: role-gated access */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ maxWidth: 460 }}>
              <Box
                sx={{
                  flex: '0 0 auto',
                  width: 36, height: 36,
                  bgcolor: 'rgba(61,122,82,0.1)',
                  border: '1px solid rgba(61,122,82,0.25)',
                  borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <ShieldOutlinedIcon sx={{ fontSize: 18, color: 'var(--sage)' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)', mb: 0.25 }}>
                  Role-based access
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: 'var(--stone)', lineHeight: 1.55, fontWeight: 300 }}>
                  You're signed in as <Box component="span" sx={{ fontWeight: 700, color: 'var(--forest)', textTransform: 'capitalize' }}>{String(user.user_type || 'individual').replace('_', ' ')}</Box>.
                  Portals outside that role stay locked for your security.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* ── RIGHT: patient gate (UHID + password) OR portal cards ── */}
          {isPatient ? (
            <Box
              component="form"
              onSubmit={step === 'credentials' ? onUhidSubmit : onOtpSubmit}
              sx={{
                bgcolor: '#fff',
                border: '1.5px solid var(--border-l)',
                borderRadius: '18px',
                p: { xs: 2.5, md: 3.25 },
                boxShadow: '0 12px 40px rgba(21,42,30,0.06)',
                width: '100%',
                maxWidth: 380,
                ml: { xs: 0, md: 'auto' },
              }}
            >
              <Typography
                className="serif"
                sx={{
                  fontSize: { xs: 22, md: 26 },
                  fontWeight: 600,
                  color: 'var(--ink)',
                  lineHeight: 1.2,
                  mb: 0.75,
                }}
              >
                {step === 'credentials' ? 'Verify your UHID' : 'Enter the OTP'}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: 'var(--stone)', mb: 3, fontWeight: 300, lineHeight: 1.55 }}>
                {step === 'credentials'
                  ? 'Reports contain protected health information. Confirm your UHID and password to open your personal dashboard.'
                  : `We've sent a 6-digit OTP to ${maskedPhone || 'your registered mobile'}. Enter it below to continue.`}
              </Typography>

              {authErr && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '12px' }}>
                  {authErr}
                </Alert>
              )}

              {step === 'credentials' ? (
                <>
                  <TextField
                    fullWidth
                    label="UHID"
                    placeholder="UH-XXXXXX"
                    value={uhid}
                    onChange={(e) => setUhid(e.target.value)}
                    disabled={verifying}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeOutlinedIcon sx={{ fontSize: 18, color: 'var(--stone)' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    type={showPwd ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    disabled={verifying}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPwd((v) => !v)}
                            edge="end"
                            size="small"
                            aria-label={showPwd ? 'Hide password' : 'Show password'}
                          >
                            {showPwd
                              ? <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                              : <VisibilityOutlinedIcon    sx={{ fontSize: 20 }} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2.5 }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={verifying}
                    sx={{
                      bgcolor: 'var(--forest)',
                      color: '#fff',
                      borderRadius: 100,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: 15,
                      '&:hover': { bgcolor: 'var(--ink)' },
                    }}
                  >
                    {verifying ? 'Sending OTP…' : 'Continue →'}
                  </Button>

                  <Typography
                    sx={{
                      fontSize: 12.5,
                      color: 'var(--stone)',
                      mt: 2.5,
                      textAlign: 'center',
                      fontWeight: 300,
                    }}
                  >
                    Don't have a UHID yet? Visit any TrustLab centre with a valid ID
                    to get one.
                  </Typography>
                </>
              ) : (
                <>
                  <Box sx={{ mb: 2.5 }}>
                    <OtpInput
                      value={otp}
                      onChange={setOtp}
                      disabled={verifyingOtp}
                      autoFocus
                    />
                  </Box>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={verifyingOtp}
                    sx={{
                      bgcolor: 'var(--forest)',
                      color: '#fff',
                      borderRadius: 100,
                      py: 1.25,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: 15,
                      mb: 1.5,
                      '&:hover': { bgcolor: 'var(--ink)' },
                    }}
                  >
                    {verifyingOtp ? 'Verifying…' : 'Verify & Open My Reports →'}
                  </Button>
                  <Button
                    fullWidth
                    disabled={verifyingOtp}
                    onClick={() => {
                      setStep('credentials');
                      setOtp('');
                      setAuthErr('');
                    }}
                    sx={{ textTransform: 'none', fontSize: 13, color: 'var(--stone)' }}
                  >
                    ← Re-enter password
                  </Button>
                </>
              )}
            </Box>
          ) : (
          <Stack spacing={2}>
            {portals.map((p) => {
              const Icon = p.icon;
              const locked = !p.enabled;
              return (
                <Box
                  key={p.id}
                  onClick={() => onCardClick(p)}
                  sx={{
                    position: 'relative',
                    bgcolor: locked ? 'rgba(255,255,255,0.6)' : '#fff',
                    border: '1.5px solid var(--border-l)',
                    borderRadius: '18px',
                    p: { xs: 2.25, md: 2.75 },
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    transition: 'all 0.2s ease',
                    opacity: locked ? 0.72 : 1,
                    '&:hover': {
                      borderColor: locked ? 'var(--border-l)' : p.accent,
                      transform: locked ? 'none' : 'translateY(-2px)',
                      boxShadow: locked ? 'none' : '0 10px 26px rgba(21,42,30,0.08)',
                    },
                  }}
                >
                  {/* Icon tile — muted when locked */}
                  <Box
                    sx={{
                      flex: '0 0 auto',
                      width: 48, height: 48,
                      bgcolor: locked ? 'rgba(107,107,101,0.08)' : `${p.accent}15`,
                      border: `1.5px solid ${locked ? 'rgba(107,107,101,0.18)' : `${p.accent}40`}`,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon sx={{ fontSize: 24, color: locked ? 'var(--stone)' : p.accent }} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 16, md: 17 },
                          fontWeight: 700,
                          color: 'var(--ink)',
                        }}
                      >
                        {p.title}
                      </Typography>
                      {locked && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          sx={{
                            bgcolor: 'rgba(107,107,101,0.1)',
                            color: 'var(--stone)',
                            px: 0.9, py: 0.25,
                            borderRadius: 100,
                          }}
                        >
                          <LockOutlinedIcon sx={{ fontSize: 11 }} />
                          <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>
                            LOCKED
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    <Typography
                      sx={{
                        fontSize: 13,
                        color: 'var(--stone)',
                        lineHeight: 1.55,
                        fontWeight: 300,
                        mb: locked ? 0 : 1.5,
                      }}
                    >
                      {p.desc}
                    </Typography>
                    {!locked && (
                      <Button
                        size="small"
                        // The outer Box onClick already navigates, so this
                        // button is purely visual — pointerEvents none so a
                        // button-on-button double-fire can't happen.
                        sx={{
                          bgcolor: 'var(--forest)',
                          color: '#fff',
                          borderRadius: 100,
                          px: 2, py: 0.75,
                          fontSize: 12.5,
                          fontWeight: 700,
                          textTransform: 'none',
                          pointerEvents: 'none',
                          '&:hover': { bgcolor: 'var(--ink)' },
                        }}
                      >
                        Open →
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
          )}
        </Box>
      </Container>

      {/* Forced change-password modal — pops after a successful
          UHID login when the user is still on the default
          'trustlab@2026' password. Once they submit a new
          password the modal closes and we navigate forward. */}
      <ChangePasswordModal
        open={showChangePwd}
        onSuccess={() => {
          setShowChangePwd(false);
          showToast('Welcome — your reports are ready');
          navigate(ROUTES.DASHBOARD);
        }}
      />
    </Box>
  );
};

export default Reports;
